import { Close } from "@/components/Icons/utility/Close.tsx";
import { AdultAgeSelect } from "@/components/Inputs/AdultAgeSelect/AdultAgeSelect.tsx";
import { ChildrenAgeSelect } from "@/components/Inputs/ChildrenAgeSelect/ChildrenAgeSelect.tsx";
import NumInput from "@/components/Inputs/NumInput/NumInput.tsx";
import { useGetExtrasTranslated } from "@/components/ReservationOptions/hooks/useGetExtrasTranslated.ts";
import { CartAccommodationItemsProps } from "@/components/ReservationSidebar/components/CartAccommodationItemsProps.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { handleGTMRemoveAccommodationFromCart } from "@/helpers/gtm/handleGTMRemoveAccommodationFromCart.ts";
import { RatePlanMerged } from "@/helpers/interfaces/RatePlanMerged.ts";
import { useGetRGTranslated } from "@/helpers/translation/useGetRGTranslated.ts";
import { useGetRPTranslated } from "@/helpers/translation/useGetRPTranslated.ts";
import { InputType, MandatoryExtra, RateSellMode } from "@/helpers/types/types.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import { ExtendedOption } from "@/stores/EngineStore/interfaces/ExtendedOption.ts";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const CartAccommodationItems = (props: CartAccommodationItemsProps) => {
  const { t } = useTranslation();
  const getExtraTranslated = useGetExtrasTranslated();
  const { getTranslateRGData } = useGetRGTranslated();
  const { getTranslateRPData } = useGetRPTranslated();

  const {
    cartAccommodationItems,
    setCartItemAdults,
    setCartItemChild,
    removeCartItem,
    updateCartItemOptions,
    calculatedCartData,
  } = useEngineStore(
    useShallow((state) => ({
      cartAccommodationItems: state.cartAccommodationItems,
      setCartItemAdults: state.setCartItemAdults,
      setCartItemChild: state.setCartItemChild,
      removeCartItem: state.removeCartItem,
      updateCartItemOptions: state.updateCartItemOptions,
      calculatedCartData: state.calculatedCartData,
    })),
  );

  const guestTotalCapacity = (RP: RatePlanMerged): number => {
    if (RP.rateGroup?.guestCapacity && RP.rateGroup?.guestAdditionalCapacity) {
      return RP.rateGroup?.guestCapacity + RP.rateGroup?.guestAdditionalCapacity;
    }
    return RP.rateGroup.guestCapacity;
  };

  const getPerPersonDefaultPrice = (CAI: CartAccommodationItem): number => {
    const rp = CAI.ratePlan;
    const people = CAI.adults.length + CAI.children.length;

    if (rp.rateSellMode === RateSellMode.PerPerson) {
      return (
        rp.childRatePlans?.find((crp) => crp.occupancy === people)?.defaultPriceBreakdown?.withDiscount ||
        rp.defaultPriceBreakdown?.withDiscount
      );
    }
    return rp.defaultPriceBreakdown?.withDiscount;
  };

  const renderCartAccommodationItem = (CAI: CartAccommodationItem) => {
    const { name: RGName } = getTranslateRGData(CAI.ratePlan.rateGroup);
    const { name: RPName } = getTranslateRPData(CAI.ratePlan);

    const calculatedAcc = calculatedCartData?.accommodations?.find((acc) => acc.identifier === CAI.id);
    const basePrice = calculatedAcc?.rateBasePrice || getPerPersonDefaultPrice(CAI);

    if (CAI) {
      return (
        <>
          <div className="rateWrap c-sidebar-rate-wrapper">
            <div className="c-sidebar-rate">
              <div className="c-sidebar-rate__top">
                <h4 className="rateName c-sidebar-rate__title u-h6 u-fw-600">
                  {RGName} - {RPName}
                </h4>
                <div className="c-sidebar-rate__details">
                  <div className="u-h6 u-fw-600 c-sidebar-rate__details-price">
                    {formatCurrency(basePrice)}
                  </div>
                  {props.hideRemoveRate ? (
                    <div className="c-sidebar-rate__details-clear">
                      <button
                        className="c-clear-button"
                        onClick={() => {
                          removeCartItem(CAI.id);
                          handleGTMRemoveAccommodationFromCart(CAI);
                        }}
                      >
                        <Close />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="c-sidebar-rate__bottom">
                <div className="c-sidebar-rate__quantities">
                  <NumInput
                    label={t("adults")}
                    onChange={(num) => setCartItemAdults(num, CAI.id)}
                    value={CAI.adults.length}
                    decrementDisabled={CAI.adults.length <= 1}
                    incrementDisabled={
                      CAI.adults.length + CAI.children.length >= guestTotalCapacity(CAI.ratePlan)
                    }
                    disabled={props.isReserved}
                    min={1}
                    max={guestTotalCapacity(CAI.ratePlan) - CAI.children.length}
                  />
                  <NumInput
                    label={t("children")}
                    onChange={(num) => setCartItemChild(num, CAI.id)}
                    value={CAI.children.length}
                    incrementDisabled={
                      CAI.adults.length + CAI.children.length >= guestTotalCapacity(CAI.ratePlan)
                    }
                    disabled={props.isReserved}
                    max={guestTotalCapacity(CAI.ratePlan) - CAI.adults.length}
                  />
                </div>
              </div>
            </div>
            {CAI.ratePlan.rateGroup.isParcel && <AdultAgeSelect cartItem={CAI} />}
            <ChildrenAgeSelect cartItem={CAI} />
          </div>
        </>
      );
    }
  };

  const renderOptionalExtras = (options: ExtendedOption[], cartItemId: string) => {
    const calculatedAccExtras =
      calculatedCartData?.accommodations?.find((acc) => acc.identifier === cartItemId)?.extras || [];

    if (options.length) {
      return (
        <div className="optionsWrap c-sidebar-option-wrapper">
          {options?.map((option) => {
            const calculatedExtra = calculatedAccExtras?.find((extra) => extra.id === option.id);
            const calculatedQuantity =
              option.inputType !== InputType.Checkbox &&
              calculatedExtra &&
              calculatedExtra.calculatedQuantity > 1 &&
              calculatedExtra.calculatedQuantity;
            const totalExtraPrice =
              typeof calculatedExtra?.total === "number"
                ? calculatedExtra?.total
                : option.price * option.value;
            const { name } = getExtraTranslated(option);

            return (
              <div key={option.id} className="option c-sidebar-option">
                <div className="c-sidebar-option__details">
                  <p className="rateName c-sidebar-option__details-title u-p0">
                    {calculatedQuantity && <span>{calculatedQuantity}</span>}
                    {name}
                  </p>
                  <div className="c-sidebar-option__details-price u-h6 u-fw-600">
                    {formatCurrency(totalExtraPrice)}
                  </div>
                  <div className="c-sidebar-option__details-clear">
                    <button
                      className="c-clear-button"
                      onClick={() => updateCartItemOptions({ ...option, value: 0 }, cartItemId)}
                    >
                      <Close />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  };

  const renderMandatoryExtras = (mandatoryExtras: MandatoryExtra[], cartItemId: string) => {
    const calculatedAccExtras =
      calculatedCartData?.accommodations?.find((acc) => acc.identifier === cartItemId)?.extras || [];
    const cartItemAcc = cartAccommodationItems.find((CAI) => CAI.id === cartItemId);

    const calculatedMandatoryExtras = calculatedAccExtras.filter((CAOE) => {
      return cartItemAcc?.mandatoryExtras.some((CE) => CE.id === CAOE.id);
    });

    if (mandatoryExtras.length) {
      return (
        <div className="c-sidebar-mandatory-options">
          {mandatoryExtras?.map((extra) => {
            const calculatedExtra = calculatedMandatoryExtras.find((CE) => CE.id === extra.id);
            const shouldRenderQuantity =
              // TODO: check if this type always present on mandatoryExtras or just wrong staging data
              // extra.inputType !== InputType.Checkbox &&
              calculatedExtra?.calculatedQuantity && calculatedExtra?.calculatedQuantity > 1;
            const total = calculatedExtra?.total;
            const { name } = getExtraTranslated(extra);

            return (
              <div key={extra.id} className="option c-sidebar-mandatory-option">
                <div className="c-sidebar-mandatory-option__details">
                  <p className="rateName c-sidebar-mandatory-option__details-title u-p1">
                    {shouldRenderQuantity && <span>{calculatedExtra?.calculatedQuantity}</span>} {name}
                  </p>
                  {typeof total === "number" && <div className="c-sidebar-from">{formatCurrency(total)}</div>}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  };

  if (!cartAccommodationItems.length) {
    return (
      <div className="c-reservation-sidebar__blank-state">
        <p className="p-1">{t("noAccommodationSelected")}</p>
      </div>
    );
  }
  return cartAccommodationItems?.map((cartAccommodationItem) => {
    return (
      <Fragment key={cartAccommodationItem.id}>
        <div className="c-reservation-sidebar__info">
          {renderCartAccommodationItem(cartAccommodationItem)}
          {renderMandatoryExtras(cartAccommodationItem.mandatoryExtras, cartAccommodationItem.id)}
          {renderOptionalExtras(cartAccommodationItem.optionalExtras, cartAccommodationItem.id)}
        </div>
      </Fragment>
    );
  });
};
